<script lang="ts" setup>
  import { LazyModalSignIn, LazyModalSignUp } from '#components';

  const { isMobile } = useDeviceType();
  const gtag = useGtag();

  const uiStore = shallowRef<ReturnType<(typeof import('~/store/ui'))['useUiStore']> | null>(null);

  async function loadUiStore() {
    const { useUiStore } = await import('~/store/ui');
    uiStore.value = useUiStore();
  }

  function openSignUpModal() {
    gtag.reportGtagConversion();
    stores.modals.open('sign-up', LazyModalSignUp);
  }

  function openSignInModal() {
    gtag.reportGtagConversion();
    stores.modals.open('sign-in', LazyModalSignIn);
  }

  async function showNotificationPanel() {
    if (!uiStore.value) await loadUiStore();
    uiStore.value?.toggleNotificationPanel();
  }

  async function toggleSidebar() {
    if (!uiStore.value) await loadUiStore();
    uiStore.value?.toggleSidebar();
  }

  function openCashier() {
    gtag.reportGtagConversion();
    stores.modals.showCashierModal();
  }

  function navigateToHome() {
    const isLoggedIn = !!useCookie('idToken').value;
    const target = isLoggedIn ? { name: ERouteName.LOBBY } : { name: ERouteName.LOGGED_OUT };
    navigateTo(target);
  }
</script>

<template>
  <header
    class="@container/header fixed w-full z-20 contain-content"
    :class="{ 'top-[var(--pwa-banner-height)]': stores.user.isPwaInstallBannerVisible }"
  >
    <div class="w-full h-1.5 lg:h-2.5 bg-[#101317]" />

    <div
      class="flex justify-between items-center h-[64px] md:h-[76px] mx-1.5 lg:mx-5 px-3.5 py-2 md:px-5 md:py-3 rounded-6px bell bg-white/5 backdrop-blur-[25px]"
    >
      <h1
        v-if="!stores.user.isLoggedIn"
        class="hidden desktop-wide:block absolute ml-[50%] -translate-x-1/2 text-white font-barlow text-2xl font-extrabold leading-[1.2] uppercase"
      >
        THE ULTIMATE player vs player GAME PLATFORM
      </h1>

      <template v-if="stores.user.isLoggedIn">
        <div class="flex items-center justify-items-start gap-2">
          <CoreButton
            class="justify-center bg-white/7 shrink-0 cursor-pointer size-12 p-2 md:p-2 text-white"
            color="neutral"
            icon="i-lucide-menu"
            size="xl"
            variant="solid"
            @click="toggleSidebar"
          />
          <CoreImage
            densities="x1 x2"
            alt="maxduel logo"
            class="cursor-pointer select-none"
            sizes="xs:144px md:193px"
            src="/images/logo.webp"
            :width="isMobile ? 144 : 193"
            preload
            @click="navigateToHome"
          />
        </div>

        <div class="flex items-center gap-1">
          <AppUserBalance />
          <CoreButton v-if="!isMobile" class="h-12" @click="openCashier">
            <span class="text-white text-center font-inter text-base font-bold leading-[100%]">Deposit</span>
          </CoreButton>
          <CoreButton
            v-if="!isMobile"
            class="relative ml-1.5 size-12 p-0 md:p-0"
            color="neutral"
            @click="showNotificationPanel"
          >
            <SvgoBell class="size-5 shrink-0" />
            <span
              v-if="stores.firebase.filteredAlerts.length"
              class="absolute top-1 right-1 flex items-center justify-center rounded-full tabular-nums size-4 bg-[#DC0000] text-white text-center font-inter text-xs font-black leading-[100%]"
              :class="{
                'px-3': stores.firebase.filteredAlerts.length >= 10,
                'px-0': stores.firebase.filteredAlerts.length < 10,
              }"
            >
              {{ stores.firebase.filteredAlerts.length }}
            </span>
          </CoreButton>
        </div>

        <LazyNotificationsPanel />
      </template>
      <template v-else>
        <NuxtImg
          alt="maxduel logo"
          class="cursor-pointer"
          sizes="xs:144px md:242px"
          src="/images/logo.webp"
          preload
          :width="isMobile ? 144 : 242"
          densities="x1 x2"
          @click="navigateToHome"
        />
        <div class="flex gap-2.5 md:gap-4">
          <CoreButton color="neutral" size="lg" @click="openSignInModal">Sign In</CoreButton>
          <CoreButton color="primary" size="lg" @click="openSignUpModal">Sign Up</CoreButton>
        </div>
      </template>
    </div>
  </header>
</template>
